import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';

import {
  AnnuaireFonts,
  colors,
  fonts,
  sectionPaddingStyle,
} from '../../components/annuaire/designSystem';

// New annuaire components
import BikeWheelLoader from '../../components/annuaire/BikeWheelLoader';
import VilleBreadcrumb from '../../components/annuaire/VilleBreadcrumb';
import VilleHero from '../../components/annuaire/VilleHero';
import FiltersBar from '../../components/annuaire/FiltersBar';
import ListingCard from '../../components/annuaire/ListingCard';
import VilleMap from '../../components/annuaire/VilleMap';
import VilleSEOContent from '../../components/annuaire/VilleSEOContent';
import VillesProches from '../../components/annuaire/VillesProches';
import CityJsonLd from '../../components/annuaire/CityJsonLd';
import ReferralRequest from '../../components/repair/ReferralRequest';

// Hook
import useShopsCities from '../../hooks/useShopsCities';
import useNextSlots from '../../hooks/useNextSlots';
import { normalizeShop, slugify } from '../../utils/utils';
import { bookingGroup, isOnlineBookable } from '../../annuaire/bookingState';
import { appliqueBonusRepar } from '../../annuaire/bonusRepar';
import { parseCitySlug } from '../../annuaire/cityDept';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Filter shops based on active service filters.
 * Logic: OR within a category, AND across categories.
 */
function applyFilters(shops, filters) {
  const { vente = [], atelier = [], extra = [] } = filters;
  const hasVente = vente.length > 0;
  const hasAtelier = atelier.length > 0;
  const hasExtra = extra.length > 0;

  if (!hasVente && !hasAtelier && !hasExtra) return shops;

  return shops.filter((shop) => {
    const services = (shop.details && shop.details.services) || [];
    const matchesVente = !hasVente || vente.some((s) => services.includes(s));
    const matchesAtelier =
      !hasAtelier || atelier.some((s) => services.includes(s));
    const matchesExtra = !hasExtra || extra.some((s) => services.includes(s));
    return matchesVente && matchesAtelier && matchesExtra;
  });
}

/** Les trois tris proposés à l'utilisateur, inchangés. */
const COMPARATORS = {
  note: (a, b) => {
    const noteA = (a.details && parseFloat(a.details.note_google)) || 0;
    const noteB = (b.details && parseFloat(b.details.note_google)) || 0;
    return noteB - noteA;
  },
  avis: (a, b) => {
    const avisA = (a.details && parseInt(a.details.nb_avis_google)) || 0;
    const avisB = (b.details && parseInt(b.details.nb_avis_google)) || 0;
    return avisB - avisA;
  },
  nom: (a, b) => {
    const nameA = (a.name || '').toLowerCase();
    const nameB = (b.name || '').toLowerCase();
    return nameA.localeCompare(nameB, 'fr');
  },
};

/**
 * Trie les fiches : le groupe de réservation d'abord, le tri choisi ensuite.
 *
 * FIXERLIGHT-050 — l'API rend déjà son listing en trois groupes (instantané,
 * réservable en ligne, le reste), mais cette page le re-triait à plat par la
 * note et écrasait donc cet ordre à l'affichage. Le groupe devient le PREMIER
 * critère du comparateur : le tri de l'utilisateur continue de s'appliquer,
 * mais à l'intérieur de son groupe, jamais au travers.
 *
 * Un comparateur composé plutôt que deux passes (partition puis concaténation)
 * : un seul appel à `sort`, et la stabilité d'`Array.prototype.sort` (garantie
 * depuis ES2019) conserve l'ordre d'origine de l'API à rang et critère égaux.
 *
 * Dégradation : `bookingGroup` rend 2 pour une fiche sans `fixer_lite`, donc
 * face à l'API staging en retard sur master, l'ordre se réduit à
 * « `bookable` d'abord » — exactement ce que servait déjà l'API avant ce
 * ticket.
 */
export function applySorting(shops, sort) {
  const comparator = COMPARATORS[sort];

  return [...shops].sort(
    (a, b) =>
      bookingGroup(a) - bookingGroup(b) || (comparator ? comparator(a, b) : 0),
  );
}

/**
 * Build villeStats from actual shop data.
 */
function computeVilleStats(shops) {
  if (!shops.length) return {};

  const notes = shops
    .map((s) => s.details && parseFloat(s.details.note_google))
    .filter((n) => n && !isNaN(n));
  const totalAvis = shops.reduce((sum, s) => {
    const n = s.details && parseInt(s.details.nb_avis_google);
    return sum + (n && !isNaN(n) ? n : 0);
  }, 0);
  const allServices = new Set();
  shops.forEach((s) => {
    ((s.details && s.details.services) || []).forEach((svc) =>
      allServices.add(svc),
    );
  });

  return {
    nb_shops: shops.length,
    note_moyenne: notes.length
      ? (notes.reduce((a, b) => a + b, 0) / notes.length).toFixed(1)
      : null,
    nb_avis_total: totalAvis,
    nb_services_uniques: allServices.size,
  };
}

/**
 * Convert othersCities (string[]) from API into villesProches format
 * expected by the VillesProches component.
 */
function buildVillesProches(othersCities) {
  if (!othersCities || !othersCities.length) return [];
  return othersCities.map((cityName) => ({
    name: cityName,
    slug: slugify(cityName),
  }));
}

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const PageWrapper = styled.div`
  background-color: #ffffff;
  font-family: ${fonts.body};
  min-height: 100vh;
`;

const ListingSection = styled.section`
  margin: 0;
  ${sectionPaddingStyle}
  /* Les cartes suivent la barre des filtres de près. */
  padding-top: 24px;

  @media (min-width: 768px) {
    padding-top: 28px;
  }
`;

const ListingContainer = styled.div`
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 24px;

  @media (min-width: 768px) {
    padding: 0 32px;
  }
`;

const VoirAutres = styled.button`
  width: 100%;
  min-height: 52px;
  border-radius: 20px;
  border: 1.5px solid ${colors.borderLight};
  background: ${colors.white};
  color: ${colors.bleuFonce};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
`;

const ShopsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 48px 24px;
  font-family: ${fonts.body};
  color: ${colors.textSecondary};
  font-size: 1rem;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AnnuaireCity = () => {
  const { dept, city: citySlug } = useParams();
  const {
    shopsCities,
    othersCities,
    name,
    villeStats: apiVilleStats,
    seoVille: apiSeoVille,
    villesProches: apiVillesProches,
    deptSlug,
    isLoading,
  } = useShopsCities(parseCitySlug(citySlug));

  // Filter / Sort state
  const [filters, setFilters] = useState({ vente: [], atelier: [], extra: [] });
  const [sort, setSort] = useState('note');
  // « Réservable en ligne » : désactivé en arrivant (lot 3, validé le 22/09).
  const [enLigneSeul, setEnLigneSeul] = useState(false);
  // « Applique le Bonus Répar » : désactivé en arrivant, comme le précédent.
  const [bonusSeul, setBonusSeul] = useState(false);

  // Normalize shops: flatten details array → object
  const normalizedShops = useMemo(
    () => (shopsCities || []).map(normalizeShop),
    [shopsCities],
  );

  // Compute villeStats: use API data if available, otherwise compute from shops
  const villeStats = useMemo(() => {
    if (apiVilleStats && apiVilleStats.nb_shops) return apiVilleStats;
    return computeVilleStats(normalizedShops);
  }, [normalizedShops, apiVilleStats]);

  // Build seoVille: use API data if available, otherwise generate intro text
  const seoVille = useMemo(() => {
    if (apiSeoVille && (apiSeoVille.introduction_seo || apiSeoVille.h1_ville)) {
      return apiSeoVille;
    }
    const nbShops = villeStats.nb_shops || normalizedShops.length;
    return {
      introduction_seo:
        nbShops > 0
          ? `Trouvez le meilleur atelier ou magasin vélo à ${
              name || ''
            }. ${nbShops} établissement${nbShops > 1 ? 's' : ''} vérifié${
              nbShops > 1 ? 's' : ''
            } avec avis clients, horaires, services proposés et coordonnées. Réparation, vente, entretien et accessoires.`
          : null,
      ...(apiSeoVille || {}),
    };
  }, [apiSeoVille, name, villeStats, normalizedShops]);

  // Build villesProches: use API villes_proches if available, fallback to othersCities
  const villesProches = useMemo(() => {
    if (apiVillesProches && apiVillesProches.length > 0)
      return apiVillesProches;
    return buildVillesProches(othersCities);
  }, [apiVillesProches, othersCities]);

  // Extract lat/lng from first shop that has coordinates
  const coords = useMemo(() => {
    for (let i = 0; i < normalizedShops.length; i++) {
      const d = normalizedShops[i].details;
      if (d && d.latitude && d.longitude) {
        return { latitude: d.latitude, longitude: d.longitude };
      }
    }
    return {};
  }, [normalizedShops]);

  // Apply client-side filtering and sorting
  const filteredShops = useMemo(
    () => applySorting(applyFilters(normalizedShops, filters), sort),
    [normalizedShops, filters, sort],
  );

  // La phrase d'intro compte la ville ; le filtre, ce qui reste à l'écran.
  const nbEnLigne = useMemo(
    () => normalizedShops.filter(isOnlineBookable).length,
    [normalizedShops],
  );

  // Calculé une seule fois par liste de fiches : les comptes et le filtre
  // ci-dessous s'appuient sur cet ensemble (les fiches gardent leur identité
  // à travers le filtrage et le tri).
  const ateliersBonus = useMemo(
    () => new Set(normalizedShops.filter(appliqueBonusRepar)),
    [normalizedShops],
  );
  const nbBonus = ateliersBonus.size;

  // FIXERLIGHT-084 : les ids de TOUS les ateliers réservables de la ville,
  // pour un seul appel `next-slots` (contrat FIXERLIGHT-055, § 1 : « pas une
  // par carte »). Volontairement `normalizedShops`, pas `affiches` : le
  // filtre « réservable en ligne » et le tri du visiteur ne doivent pas
  // redéclencher l'appel (`useNextSlots` s'en charge déjà via sa clé
  // stable), et une carte qui redevient visible après un changement de
  // filtre garde son horaire sans le redemander.
  const idsReservables = useMemo(
    () =>
      normalizedShops
        .filter(isOnlineBookable)
        .map((shop) => shop.id)
        .filter(Boolean),
    [normalizedShops],
  );
  const nextSlotByShopId = useNextSlots(idsReservables);
  // Les deux filtres se cumulent : réservable en ligne ET qui applique le
  // Bonus. Le compte de chaque pastille tient donc compte de l'autre filtre
  // quand il est actif : il annonce ce que l'on obtiendrait en cliquant.
  const { affiches, nbEnLigneFiltres, nbBonusFiltres } = useMemo(() => {
    const enLigne = (shop) => isOnlineBookable(shop);
    const bonus = (shop) => ateliersBonus.has(shop);
    return {
      affiches: filteredShops.filter(
        (shop) =>
          (!enLigneSeul || enLigne(shop)) && (!bonusSeul || bonus(shop)),
      ),
      nbEnLigneFiltres: filteredShops.filter(
        (shop) => enLigne(shop) && (!bonusSeul || bonus(shop)),
      ).length,
      nbBonusFiltres: filteredShops.filter(
        (shop) => bonus(shop) && (!enLigneSeul || enLigne(shop)),
      ).length,
    };
  }, [enLigneSeul, bonusSeul, filteredShops, ateliersBonus]);
  const nbAutres = filteredShops.length - affiches.length;
  const voirAutres = (enLigneSeul || bonusSeul) && nbAutres > 0 && (
    <VoirAutres
      type="button"
      onClick={() => {
        setEnLigneSeul(false);
        setBonusSeul(false);
      }}>
      {nbAutres > 1
        ? `Voir les ${nbAutres} autres ateliers`
        : 'Voir l’autre atelier'}
    </VoirAutres>
  );

  // Le JSON-LD décrit la VILLE, pas la sélection du visiteur : il reçoit donc
  // la liste groupée mais NON filtrée, et sans le tri choisi — qui appartient
  // au visiteur, pas à la ville. Sa `position` suit donc l'ordre des groupes
  // et celui de l'API à l'intérieur, pas l'ordre exact affiché à l'écran.
  const groupedShops = useMemo(() => applySorting(normalizedShops, null), [
    normalizedShops,
  ]);

  // SEO meta
  const metaTitle =
    (seoVille && seoVille.meta_title_seo) ||
    `Ateliers vélo à ${name || ''} - Annuaire Roulez Jeunesse`;
  const metaDescription =
    (seoVille && seoVille.meta_description_seo) ||
    `Trouvez les meilleurs ateliers de réparation vélo à ${
      name || ''
    }. Comparez les avis, horaires et services.`;

  // Derive display name from slug as fallback (e.g. "la-rochelle" → "La Rochelle")
  const displayName =
    name ||
    citySlug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (isLoading) {
    return (
      <PageWrapper>
        <AnnuaireFonts />
        <CityJsonLd cityName={displayName} shops={[]} />
        <VilleBreadcrumb name={name} />
        <BikeWheelLoader />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <AnnuaireFonts />

      <Helmet
        title={metaTitle}
        meta={[
          { name: 'description', content: metaDescription },
          { property: 'og:title', content: metaTitle },
          { property: 'og:description', content: metaDescription },
          { property: 'og:type', content: 'website' },
        ]}
      />

      {/* SEO: JSON-LD structured data (BreadcrumbList, ItemList) */}
      <CityJsonLd cityName={displayName} shops={groupedShops} />

      {/* Breadcrumb: Accueil / Annuaire / Ville */}
      <VilleBreadcrumb name={name} />

      {/* Hero: H1, intro text, 4 stats */}
      <VilleHero
        name={name}
        villeStats={villeStats}
        seoVille={seoVille}
        nbEnLigne={nbEnLigne}
        nbBonus={nbBonus}
      />

      {/* Filters: 3 category dropdowns, sort, active chips */}
      <FiltersBar
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
        resultCount={affiches.length}
        enLigne={{
          count: nbEnLigneFiltres,
          actif: enLigneSeul,
          onToggle: () => setEnLigneSeul((v) => !v),
        }}
        bonus={{
          count: nbBonusFiltres,
          actif: bonusSeul,
          onToggle: () => setBonusSeul((v) => !v),
        }}
      />

      {/* Shop listings */}
      <ListingSection>
        <ListingContainer>
          {affiches.length > 0 ? (
            <ShopsList>
              {affiches.map((shop, i) => (
                <ListingCard
                  key={shop.id || i}
                  shop={shop}
                  nextSlot={nextSlotByShopId[shop.id]}
                />
              ))}
              {voirAutres}
            </ShopsList>
          ) : (
            <NoResults>
              Aucun atelier ne correspond à vos critères.
              <br />
              Essayez de modifier vos filtres.
              {voirAutres}
            </NoResults>
          )}
        </ListingContainer>
      </ListingSection>

      {/* Map: Google Maps embed centered on the city */}
      <VilleMap
        name={name}
        latitude={coords.latitude}
        longitude={coords.longitude}
      />

      {/* SEO content block */}
      <VilleSEOContent name={name} seoVille={seoVille} />

      {/* Nearby cities grid */}
      <VillesProches name={name} villesProches={villesProches} />

      {/* Referral banner */}
      <ListingContainer style={{ marginBottom: '48px' }}>
        <ReferralRequest />
      </ListingContainer>
    </PageWrapper>
  );
};

export default AnnuaireCity;
